import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Select, Card, Space, Typography, Tag, Spin, Empty, DatePicker,
  Tooltip, Button, Dropdown, Checkbox, Modal, Badge
} from 'antd';
import {
  LineChartOutlined,
  BarChartOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid,
  Tooltip as RechartsTooltip, Legend, ResponsiveContainer, ComposedChart
} from 'recharts';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import { businessDomainApi, dataInsightApi } from '../../services/api';

const { Title, Text } = Typography;
const { RangePicker } = DatePicker;

// ---- 指标定义（3维度 × 5指标 = 15项预设）----
const METRIC_DIMENSIONS = [
  {
    key: 'doc',
    label: '单据维度',
    metrics: [
      { key: 'doc_no_risk_precision', label: '无风险精确率' },
      { key: 'doc_no_risk_recall', label: '无风险召回率' },
      { key: 'doc_risk_precision', label: '有风险精确率' },
      { key: 'doc_risk_recall', label: '有风险召回率' },
      { key: 'doc_accuracy', label: '准确率' },
    ],
  },
  {
    key: 'rule_avg',
    label: '规则平均维度',
    metrics: [
      { key: 'rule_avg_no_risk_precision', label: '无风险精确率' },
      { key: 'rule_avg_no_risk_recall', label: '无风险召回率' },
      { key: 'rule_avg_risk_precision', label: '有风险精确率' },
      { key: 'rule_avg_risk_recall', label: '有风险召回率' },
      { key: 'rule_avg_accuracy', label: '准确率' },
    ],
  },
  {
    key: 'rule_detail',
    label: '规则明细维度',
    metrics: [
      { key: 'rule_detail_no_risk_precision', label: '无风险精确率' },
      { key: 'rule_detail_no_risk_recall', label: '无风险召回率' },
      { key: 'rule_detail_risk_precision', label: '有风险精确率' },
      { key: 'rule_detail_risk_recall', label: '有风险召回率' },
      { key: 'rule_detail_accuracy', label: '准确率' },
    ],
  },
];

// 默认选中的指标：单据维度全部5项
const DEFAULT_METRICS = [
  'doc_no_risk_precision',
  'doc_no_risk_recall',
  'doc_risk_precision',
  'doc_risk_recall',
  'doc_accuracy',
];

// 评估类型定义
const CATEGORY_OPTIONS = [
  { value: 'BASELINE', label: '基线评估', color: '#4F8EF7' },
  { value: 'ACCEPTANCE', label: '验收评估', color: '#FA8C16' },
  { value: 'PERIODIC', label: '线上评估', color: '#52C41A' },
];

const CATEGORY_COLOR_MAP = {
  BASELINE: { color: '#4F8EF7', label: '基线' },
  ACCEPTANCE: { color: '#FA8C16', label: '评估验收' },
  PERIODIC: { color: '#52C41A', label: '线上周期' },
};

// 图表颜色池（用于不同指标）
const LINE_COLORS = [
  '#4F8EF7', '#FF6B6B', '#52C41A', '#FAAD14', '#722ED1',
  '#13C2C2', '#EB2F96', '#FA8C16', '#A0D911', '#1890FF',
  '#F5222D', '#52C41A', '#FADB14', '#2F54EB', '#722ED1',
];

// ---- 自定义指标 Mock 数据 ----
const CUSTOM_METRIC_OPTIONS = [
  { key: 'custom_spam_recall', label: '垃圾内容召回率', desc: '识别垃圾类内容的召回能力' },
  { key: 'custom_violence_precision', label: '暴力内容精确率', desc: '暴力类命中的精准度' },
  { key: 'custom_politics_accuracy', label: '政治敏感准确率', desc: '政治类内容综合准确率' },
  { key: 'custom_porn_recall', label: '色情内容召回率', desc: '色情类内容的识别率' },
  { key: 'custom_ad_precision', label: '广告内容精确率', desc: '广告类误报控制精确率' },
  { key: 'custom_abuse_f1', label: '辱骂内容F1', desc: '辱骂类综合F1分数' },
];

// ---- 全局指标选择下拉组件 ----
function GlobalMetricSelector({ selectedMetrics, onChange }) {
  const allMetricKeys = METRIC_DIMENSIONS.flatMap(d => d.metrics.map(m => m.key));
  const allMetricMap = {};
  METRIC_DIMENSIONS.forEach(d => d.metrics.forEach(m => { allMetricMap[m.key] = `${d.label} · ${m.label}`; }));

  const menu = (
    <div
      style={{
        background: '#fff',
        borderRadius: 8,
        boxShadow: '0 4px 20px rgba(0,0,0,0.12)',
        padding: '12px 0',
        minWidth: 280,
        maxHeight: 400,
        overflowY: 'auto',
      }}
    >
      {METRIC_DIMENSIONS.map(dim => (
        <div key={dim.key} style={{ padding: '4px 16px' }}>
          <div style={{ fontWeight: 600, fontSize: 12, color: '#888', marginBottom: 4, marginTop: 4 }}>
            {dim.label}
          </div>
          {dim.metrics.map(m => (
            <div
              key={m.key}
              style={{ padding: '4px 8px', cursor: 'pointer', borderRadius: 4 }}
              onClick={() => {
                const next = selectedMetrics.includes(m.key)
                  ? selectedMetrics.filter(k => k !== m.key)
                  : [...selectedMetrics, m.key];
                if (next.length > 0) onChange(next);
              }}
            >
              <Checkbox checked={selectedMetrics.includes(m.key)} style={{ pointerEvents: 'none' }}>
                <span style={{ fontSize: 13 }}>{m.label}</span>
              </Checkbox>
            </div>
          ))}
        </div>
      ))}
      <div style={{ borderTop: '1px solid #f0f0f0', padding: '8px 16px', marginTop: 4, display: 'flex', gap: 8 }}>
        <Button size="small" onClick={() => onChange(DEFAULT_METRICS)}>重置</Button>
        <Button size="small" type="primary" onClick={() => onChange(allMetricKeys)}>全选</Button>
      </div>
    </div>
  );

  return (
    <Dropdown overlay={menu} trigger={['click']} placement="bottomLeft">
      <Button icon={<FilterOutlined />} size="small">
        指标选择 ({selectedMetrics.length})
      </Button>
    </Dropdown>
  );
}

// ---- 效果水位趋势图（左图）----
function WatermarkChart({ scenarioId, scenarioName, selectedCategories, dateRange, selectedMetrics }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [activePoint, setActivePoint] = useState(null);
  // 自定义指标弹窗
  const [customModalOpen, setCustomModalOpen] = useState(false);
  const [selectedCustomMetrics, setSelectedCustomMetrics] = useState([]); // 默认不选中

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dataInsightApi.getWatermarkTrend({
        scenarioId,
        startDate: dateRange[0]?.format('YYYY-MM-DD'),
        endDate: dateRange[1]?.format('YYYY-MM-DD'),
      });
      setData(res.list || []);
      setActivePoint(null);
    } finally {
      setLoading(false);
    }
  }, [scenarioId, dateRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const allMetricMap = {};
  METRIC_DIMENSIONS.forEach(dim => dim.metrics.forEach(m => {
    allMetricMap[m.key] = `${dim.label} · ${m.label}`;
  }));
  // 自定义指标也加入 map
  CUSTOM_METRIC_OPTIONS.forEach(m => { allMetricMap[m.key] = `自定义 · ${m.label}`; });

  // 合并选中的指标（预设 + 自定义）
  const allSelectedMetrics = useMemo(
    () => [...selectedMetrics, ...selectedCustomMetrics],
    [selectedMetrics, selectedCustomMetrics]
  );

  // 过滤出选中评估类型的数据
  const filteredData = useMemo(() => 
    data.filter(d => selectedCategories.includes(d.setCategory)),
    [data, selectedCategories]
  );

  // 构建图表数据：按日期合并，每条线代表一个指标（不按评估类型拆分）
  const chartData = useMemo(() => 
    filteredData.map(d => {
      const row = { date: d.date, _raw: d };
      selectedMetrics.forEach(key => {
        row[key] = d[key] !== undefined ? +(d[key] * 100).toFixed(2) : null;
      });
      // 自定义指标注入 mock 值（基于 benchmarkId 做确定性随机）
      selectedCustomMetrics.forEach((key, idx) => {
        const seed = (d.benchmarkId || 1) * 7 + idx * 13;
        row[key] = +(65 + (seed % 25) + Math.sin(seed) * 5).toFixed(2);
      });
      return row;
    }),
    [filteredData, selectedMetrics, selectedCustomMetrics]
  );

  // 图表级点击：捕获点击的数据点
  const handleChartClick = useCallback((chartEvent) => {
    if (chartEvent && chartEvent.activePayload && chartEvent.activePayload.length > 0) {
      const raw = chartEvent.activePayload[0]?.payload?._raw;
      if (raw) setActivePoint(raw);
    }
  }, []);

  // 散点样式：按评估类型着色
  const makeDot = (color) => (props) => {
    const { cx, cy, payload } = props;
    const raw = payload?._raw;
    if (!raw) return null;
    const catColor = CATEGORY_COLOR_MAP[raw.setCategory]?.color || color;
    const isActive = activePoint && raw.benchmarkId === activePoint.benchmarkId;
    return (
      <circle
        key={`dot-${cx}-${cy}`}
        cx={cx} cy={cy}
        r={isActive ? 7 : 4}
        fill={catColor}
        stroke={isActive ? '#333' : '#fff'}
        strokeWidth={isActive ? 2 : 1.5}
        style={{ cursor: 'pointer' }}
      />
    );
  };

  // Tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const raw = payload[0]?.payload?._raw;
    return (
      <div style={{
        background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8,
        padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.12)', maxWidth: 280,
        pointerEvents: 'none',
      }}>
        <div style={{ fontWeight: 600, marginBottom: 4, color: '#333', fontSize: 12 }}>{label}</div>
        {raw && (
          <div style={{ marginBottom: 5 }}>
            <Tag color={CATEGORY_COLOR_MAP[raw.setCategory]?.color} style={{ fontSize: 10 }}>
              {CATEGORY_COLOR_MAP[raw.setCategory]?.label}
            </Tag>
            <span style={{ fontSize: 11, color: '#888' }}>{raw.benchmarkName}</span>
          </div>
        )}
        {payload.map(p => (
          <div key={p.dataKey} style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 11, marginBottom: 2 }}>
            <span style={{ color: p.color }}>{allMetricMap[p.dataKey] || p.dataKey}</span>
            <span style={{ fontWeight: 600 }}>{p.value !== null ? `${p.value}%` : '-'}</span>
          </div>
        ))}
        <div style={{ marginTop: 5, fontSize: 10, color: '#999' }}>点击数据点查看 Benchmark 详情</div>
      </div>
    );
  };

  return (
    <Card
      size="small"
      style={{ flex: 1, minWidth: 0 }}
      bodyStyle={{ padding: '12px 16px' }}
      title={null}
    >
      {/* 标题行 */}
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <LineChartOutlined style={{ color: '#4F8EF7' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>效果水位趋势</span>
          <div style={{ display: 'flex', gap: 4 }}>
            {selectedCategories.map(cat => (
              <Tag key={cat} color={CATEGORY_COLOR_MAP[cat]?.color} style={{ fontSize: 11, margin: 0 }}>
                {CATEGORY_COLOR_MAP[cat]?.label}
              </Tag>
            ))}
          </div>
          {/* 自定义指标按钮 */}
          <div style={{ marginLeft: 'auto' }}>
            <Badge count={selectedCustomMetrics.length} size="small" offset={[-4, 4]}>
              <Button
                size="small"
                style={{
                  borderColor: '#ff4d4f',
                  color: '#ff4d4f',
                  fontSize: 12,
                }}
                onClick={() => setCustomModalOpen(true)}
              >
                自定义指标
              </Button>
            </Badge>
          </div>
        </div>
      </div>

      {/* 自定义指标选择弹窗 */}
      <Modal
        title="自定义指标选择"
        open={customModalOpen}
        onCancel={() => setCustomModalOpen(false)}
        footer={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <span style={{ fontSize: 12, color: '#888' }}>已选 {selectedCustomMetrics.length} / {CUSTOM_METRIC_OPTIONS.length} 项</span>
            <div style={{ display: 'flex', gap: 8 }}>
              <Button size="small" onClick={() => setSelectedCustomMetrics([])}>清空</Button>
              <Button size="small" type="primary" onClick={() => setCustomModalOpen(false)}>确定</Button>
            </div>
          </div>
        }
        width={480}
      >
        <div style={{ marginBottom: 8, fontSize: 12, color: '#888' }}>
          以下为自定义扩展指标，默认不启用，勾选后加入图表展示
        </div>
        {CUSTOM_METRIC_OPTIONS.map(m => (
          <div
            key={m.key}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              padding: '8px 12px',
              borderRadius: 6,
              marginBottom: 4,
              background: selectedCustomMetrics.includes(m.key) ? '#f0f7ff' : '#fafafa',
              border: `1px solid ${selectedCustomMetrics.includes(m.key) ? '#91caff' : '#f0f0f0'}`,
              cursor: 'pointer',
            }}
            onClick={() => {
              setSelectedCustomMetrics(prev =>
                prev.includes(m.key)
                  ? prev.filter(k => k !== m.key)
                  : [...prev, m.key]
              );
            }}
          >
            <div>
              <Checkbox
                checked={selectedCustomMetrics.includes(m.key)}
                style={{ marginRight: 8, pointerEvents: 'none' }}
              />
              <span style={{ fontSize: 13, fontWeight: 500 }}>{m.label}</span>
            </div>
            <span style={{ fontSize: 11, color: '#aaa' }}>{m.desc}</span>
          </div>
        ))}
      </Modal>

      {/* 点击后固定展示的 Benchmark 信息条 */}
      {activePoint && (
        <div style={{
          background: '#f0f7ff',
          border: '1px solid #91caff',
          borderRadius: 6,
          padding: '7px 12px',
          marginBottom: 8,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          flexWrap: 'wrap',
          gap: 8,
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <Tag color={CATEGORY_COLOR_MAP[activePoint.setCategory]?.color} style={{ margin: 0 }}>
              {CATEGORY_COLOR_MAP[activePoint.setCategory]?.label}
            </Tag>
            <span style={{ fontSize: 13, fontWeight: 600 }}>{activePoint.benchmarkName}</span>
            <span style={{ fontSize: 12, color: '#888' }}>{activePoint.date}</span>
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            <Button
              type="primary"
              size="small"
              onClick={() => navigate(`/benchmark/${activePoint.benchmarkId}`)}
            >
              查看 Benchmark 详情
            </Button>
            <Button size="small" onClick={() => setActivePoint(null)}>关闭</Button>
          </div>
        </div>
      )}

      <Spin spinning={loading}>
        {chartData.length === 0 ? (
          <Empty description="暂无数据" style={{ padding: '40px 0' }} />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <LineChart
              data={chartData}
              margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
              onClick={handleChartClick}
              style={{ cursor: 'pointer' }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis
                dataKey="date"
                tick={{ fontSize: 11 }}
                angle={-30}
                textAnchor="end"
                height={50}
              />
              <YAxis
                tickFormatter={v => `${v}%`}
                domain={[40, 100]}
                tick={{ fontSize: 11 }}
                width={48}
              />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend
                formatter={key => allMetricMap[key] || key}
                wrapperStyle={{ fontSize: 11, paddingTop: 8 }}
              />
              {/* 每个指标一条线（预设 + 自定义），不按评估类型拆分 */}
              {allSelectedMetrics.map((key, i) => (
                <Line
                  key={key}
                  type="monotone"
                  dataKey={key}
                  stroke={LINE_COLORS[i % LINE_COLORS.length]}
                  strokeWidth={2}
                  strokeDasharray={selectedCustomMetrics.includes(key) ? '6 3' : undefined}
                  dot={makeDot(LINE_COLORS[i % LINE_COLORS.length])}
                  activeDot={{ r: 6, strokeWidth: 2, stroke: '#fff' }}
                  connectNulls={false}
                />
              ))}
            </LineChart>
          </ResponsiveContainer>
        )}
      </Spin>
    </Card>
  );
}

// ---- 样本使用情况图（右图）----
function SampleUsageChart({ scenarioId, selectedCategories, monthRange }) {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await dataInsightApi.getSampleUsageByCategory({
        scenarioId,
        startMonth: monthRange[0]?.format('YYYY-MM'),
        endMonth: monthRange[1]?.format('YYYY-MM'),
      });
      setData(res.list || []);
    } finally {
      setLoading(false);
    }
  }, [scenarioId, monthRange]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const totalItem = payload.find(p => p.dataKey === 'totalSamples');
    const usedItem = payload.find(p => p.dataKey === 'usedSamples');
    const rateItem = payload.find(p => p.dataKey === 'usageRate');
    // 取原始数据中的评估类型信息（用于展示）
    const raw = payload[0]?.payload?._raw;
    return (
      <div style={{ background: '#fff', border: '1px solid #e8e8e8', borderRadius: 8, padding: '10px 14px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}>
        <div style={{ fontWeight: 600, marginBottom: 6 }}>{label}</div>
        {raw?.setCategory && (
          <div style={{ marginBottom: 5 }}>
            <Tag color={CATEGORY_COLOR_MAP[raw.setCategory]?.color} style={{ fontSize: 10 }}>
              {CATEGORY_COLOR_MAP[raw.setCategory]?.label}
            </Tag>
          </div>
        )}
        <div style={{ fontSize: 12, display: 'flex', flexDirection: 'column', gap: 4 }}>
          {totalItem && <div style={{ color: '#4F8EF7' }}>样本总数：{totalItem.value?.toLocaleString() || '-'}</div>}
          {usedItem && <div style={{ color: '#52C41A' }}>已执行样本：{usedItem.value?.toLocaleString() || '-'}</div>}
          {rateItem && <div style={{ color: '#FA8C16', fontWeight: 600 }}>样本使用率：{rateItem.value}%</div>}
        </div>
      </div>
    );
  };

  // 聚合所有选中评估类型的数据（按月合并），不拆分图例
  const chartData = useMemo(() => {
    // 先收集所有月份
    const monthSet = new Set();
    data.forEach(d => monthSet.add(d.month));
    const months = Array.from(monthSet).sort();

    return months.map(month => {
      const monthItems = data.filter(d => d.month === month);
      // 只保留选中评估类型的数据点
      const filtered = monthItems.filter(d => {
        // 旧格式：直接有 byCategory
        if (d.byCategory) {
          return true;
        }
        return true;
      });

      // 把所有选中类型的数据聚合到一行
      let totalSamples = 0;
      let usedSamples = 0;

      filtered.forEach(d => {
        if (d.byCategory) {
          // 按类型分组格式
          selectedCategories.forEach(cat => {
            const catData = d.byCategory?.[cat];
            if (catData) {
              totalSamples += catData.totalSamples || 0;
              usedSamples += catData.usedSamples || 0;
            }
          });
        } else {
          // 旧格式直接累加
          totalSamples += d.totalSamples || 0;
          usedSamples += d.usedSamples || 0;
        }
      });

      const usageRate = totalSamples > 0 ? +(usedSamples / totalSamples * 100).toFixed(1) : 0;
      return { month, totalSamples, usedSamples, usageRate };
    });
  }, [data, selectedCategories]);

  return (
    <Card
      size="small"
      style={{ width: 420, flexShrink: 0 }}
      bodyStyle={{ padding: '12px 16px' }}
      title={null}
    >
      <div style={{ marginBottom: 10 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
          <BarChartOutlined style={{ color: '#52C41A' }} />
          <span style={{ fontSize: 13, fontWeight: 600 }}>评测样本使用</span>
        </div>
      </div>
      <Spin spinning={loading}>
        {chartData.length === 0 ? (
          <Empty description="暂无数据" style={{ padding: '40px 0' }} />
        ) : (
          <ResponsiveContainer width="100%" height={280}>
            <ComposedChart data={chartData} margin={{ top: 10, right: 50, left: 0, bottom: 20 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="month" tick={{ fontSize: 11 }} angle={-30} textAnchor="end" height={50} />
              <YAxis yAxisId="left" tick={{ fontSize: 11 }} width={50} />
              <YAxis yAxisId="right" orientation="right" tickFormatter={v => `${v}%`} domain={[0, 100]} tick={{ fontSize: 11 }} width={44} />
              <RechartsTooltip content={<CustomTooltip />} />
              <Legend wrapperStyle={{ fontSize: 11, paddingTop: 8 }} />
              <Bar yAxisId="left" dataKey="totalSamples" name="样本总数" fill="#E8F0FE" radius={[3, 3, 0, 0]} />
              <Bar yAxisId="left" dataKey="usedSamples" name="已执行样本" fill="#4F8EF7" radius={[3, 3, 0, 0]} />
              <Line yAxisId="right" type="monotone" dataKey="usageRate" name="使用率%" stroke="#FA8C16" strokeWidth={2.5} dot={{ r: 4, fill: '#FA8C16' }} activeDot={{ r: 6 }} />
            </ComposedChart>
          </ResponsiveContainer>
        )}
      </Spin>
    </Card>
  );
}

// ---- 单个场景卡片 ----
function ScenarioCard({ scenario, selectedCategories, dateRange, monthRange, selectedMetrics }) {
  const typeLabel = scenario.type === 'MACHINE_AUDIT' ? '机审场景' : '要素抽取';
  const typeColor = scenario.type === 'MACHINE_AUDIT' ? 'blue' : 'green';

  return (
    <Card
      style={{ marginBottom: 20, borderRadius: 10, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}
      bodyStyle={{ padding: 16 }}
    >
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 14 }}>
        <Title level={5} style={{ margin: 0 }}>{scenario.name}</Title>
        <Tag color={typeColor}>{typeLabel}</Tag>
      </div>
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <WatermarkChart 
          scenarioId={scenario.id} 
          scenarioName={scenario.name}
          selectedCategories={selectedCategories}
          dateRange={dateRange}
          selectedMetrics={selectedMetrics}
        />
        <SampleUsageChart 
          scenarioId={scenario.id}
          selectedCategories={selectedCategories}
          monthRange={monthRange}
        />
      </div>
    </Card>
  );
}

// ---- 主页面 ----
export default function DataInsight() {
  const [domains, setDomains] = useState([]);
  // 项目空间改为多选
  const [selectedDomainIds, setSelectedDomainIds] = useState([]);
  const [allScenarios, setAllScenarios] = useState([]);
  const [selectedScenarioIds, setSelectedScenarioIds] = useState([]);
  const [loadingScenarios, setLoadingScenarios] = useState(false);

  // ---- 全局筛选状态 ----
  // 评估类型：默认全选
  const [selectedCategories, setSelectedCategories] = useState(['BASELINE', 'ACCEPTANCE', 'PERIODIC']);
  // 时间筛选：左图按日默认90天
  const [globalDateRange, setGlobalDateRange] = useState([
    dayjs().subtract(90, 'day'),
    dayjs(),
  ]);
  // 时间筛选：右图按月默认6个月
  const [globalMonthRange, setGlobalMonthRange] = useState([
    dayjs().subtract(6, 'month').startOf('month'),
    dayjs().endOf('month'),
  ]);
  // 指标选择：默认单据维度5项
  const [globalSelectedMetrics, setGlobalSelectedMetrics] = useState(DEFAULT_METRICS);

  // 加载项目空间列表
  useEffect(() => {
    businessDomainApi.getList({ pageNo: 1, pageSize: 50 }).then(res => {
      const list = res.list || [];
      setDomains(list);
      // 默认全选项目空间
      if (list.length > 0) {
        setSelectedDomainIds(list.map(d => d.id));
      }
    });
  }, []);

  // 加载业务场景列表（合并多个项目空间的场景）
  useEffect(() => {
    if (selectedDomainIds.length === 0) {
      setAllScenarios([]);
      setSelectedScenarioIds([]);
      return;
    }
    setLoadingScenarios(true);
    setSelectedScenarioIds([]);
    
    // 并行请求所有选中项目空间的场景
    Promise.all(
      selectedDomainIds.map(domainId => dataInsightApi.getScenarioList(domainId))
    ).then(results => {
      const allScenariosList = results.flatMap(res => res.list || []);
      setAllScenarios(allScenariosList);
      // 默认全选场景
      setSelectedScenarioIds(allScenariosList.map(s => s.id));
    }).finally(() => setLoadingScenarios(false));
  }, [selectedDomainIds]);

  const selectedScenarios = useMemo(
    () => allScenarios.filter(s => selectedScenarioIds.includes(s.id)),
    [allScenarios, selectedScenarioIds]
  );

  return (
    <div style={{ padding: '20px 24px', minHeight: '100vh', background: '#f5f7fa' }}>
      {/* 顶部标题 + 全局筛选 */}
      <div style={{
        background: '#fff',
        borderRadius: 10,
        padding: '16px 20px',
        marginBottom: 20,
        boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
      }}>
        {/* 第一行：标题 + 项目空间 + 业务场景 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 20, flexWrap: 'wrap', marginBottom: 12 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <div style={{ width: 36, height: 36, borderRadius: 8, background: 'linear-gradient(135deg,#4F8EF7,#7B61FF)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <LineChartOutlined style={{ color: '#fff', fontSize: 18 }} />
            </div>
            <div>
              <div style={{ fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>评测数据洞察</div>
              <div style={{ fontSize: 12, color: '#999' }}>多场景效果水位趋势 & 样本使用分析</div>
            </div>
          </div>

          <div style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Space>
              <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>项目空间</Text>
              <Select
                mode="multiple"
                style={{ minWidth: 200 }}
                value={selectedDomainIds}
                onChange={v => v.length > 0 && setSelectedDomainIds(v)}
                options={domains.map(d => ({ value: d.id, label: d.name }))}
                placeholder="选择项目空间（可多选）"
                maxTagCount={2}
              />
            </Space>
            <Space>
              <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>业务场景</Text>
              <Select
                mode="multiple"
                style={{ minWidth: 240 }}
                value={selectedScenarioIds}
                onChange={setSelectedScenarioIds}
                options={allScenarios.map(s => ({ value: s.id, label: s.name }))}
                placeholder="选择业务场景（可多选）"
                loading={loadingScenarios}
                maxTagCount={2}
              />
            </Space>
            {selectedScenarioIds.length !== allScenarios.length && allScenarios.length > 0 && (
              <Button size="small" type="link" onClick={() => setSelectedScenarioIds(allScenarios.map(s => s.id))}>
                全选
              </Button>
            )}
          </div>
        </div>

        {/* 第二行：评估类型 + 时间筛选 + 指标选择 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', paddingTop: 12, borderTop: '1px solid #f0f0f0' }}>
          {/* 评估类型多选 */}
          <Space>
            <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>评估类型</Text>
            <Select
              mode="multiple"
              style={{ minWidth: 200 }}
              value={selectedCategories}
              onChange={v => v.length > 0 && setSelectedCategories(v)}
              options={CATEGORY_OPTIONS.map(c => ({ value: c.value, label: c.label }))}
              placeholder="选择评估类型"
              maxTagCount={3}
            />
          </Space>

          {/* 时间筛选（左图-按日） */}
          <Space>
            <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>时间范围(水位)</Text>
            <RangePicker
              size="small"
              value={globalDateRange}
              onChange={v => v && setGlobalDateRange(v)}
              format="YYYY-MM-DD"
              allowClear={false}
              style={{ width: 220 }}
            />
          </Space>

          {/* 时间筛选（右图-按月） */}
          <Space>
            <Text style={{ fontSize: 13, color: '#555', whiteSpace: 'nowrap' }}>时间范围(样本)</Text>
            <RangePicker
              size="small"
              picker="month"
              value={globalMonthRange}
              onChange={v => v && setGlobalMonthRange(v)}
              format="YYYY-MM"
              allowClear={false}
              style={{ width: 180 }}
            />
          </Space>

          {/* 指标选择 */}
          <GlobalMetricSelector 
            selectedMetrics={globalSelectedMetrics} 
            onChange={setGlobalSelectedMetrics} 
          />
        </div>

        {/* 提示 */}
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 12 }}>
          <InfoCircleOutlined style={{ color: '#bbb', fontSize: 13 }} />
          <Text style={{ fontSize: 12, color: '#999' }}>
            已选 {selectedDomainIds.length} 个空间、{selectedScenarios.length} 个场景，筛选条件全局生效
          </Text>
        </div>
      </div>

      {/* 场景卡片区域 */}
      {selectedScenarios.length === 0 ? (
        <Card style={{ borderRadius: 10 }}>
          <Empty
            description={selectedDomainIds.length === 0 ? '请先选择项目空间' : '请在顶部选择至少一个业务场景'}
            style={{ padding: '60px 0' }}
          />
        </Card>
      ) : (
        selectedScenarios.map(scenario => (
          <ScenarioCard 
            key={scenario.id} 
            scenario={scenario}
            selectedCategories={selectedCategories}
            dateRange={globalDateRange}
            monthRange={globalMonthRange}
            selectedMetrics={globalSelectedMetrics}
          />
        ))
      )}
    </div>
  );
}
